import LandingNavbar from '../../components/landing/landing-navbar';
import LandingFooter from '../../components/landing/landing-footer';
import { useEffect, useState, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const ExperienceCard = ({ title, description, imagePosition = 'right', imageSrc, imageAlt, isInView }) => {
  const isRight = imagePosition === 'right';
  
  return (
    <div 
      className={`flex flex-col ${isRight ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-6 lg:gap-8 mb-12 lg:mb-16 transition-all duration-1000 ease-out`}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView 
          ? 'perspective(1000px) translateZ(0) scale(1)' 
          : 'perspective(1000px) translateZ(-300px) scale(0.6)'
      }}
    >
      <div className={`flex-1 flex flex-col justify-center ${isRight ? 'lg:pr-4' : 'lg:pl-4'}`}>
        <h2 className="text-3xl lg:text-4xl font-light mb-4" style={{ color: '#c9a980' }}>
          {title}
        </h2>
        <p className="text-gray-700 leading-relaxed mb-6 text-sm lg:text-base">
          {description}
        </p>
        <div>
          <button className="px-6 py-2.5 border-2 text-sm font-medium rounded-full transition-colors duration-300 hover:bg-[#3d3226] hover:text-white" style={{ borderColor: '#3d3226', color: '#3d3226' }}>
            DISCOVER DEAL
          </button>
        </div>
      </div>
      
      <div className="flex-1">
        <div className="relative w-full h-64 lg:h-80 xl:h-96 overflow-hidden shadow-lg">
          <img
            src={imageSrc}
            alt={imageAlt}
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  );
};

export default function ToursPage() {
  const [isVolcanoSectionInView, setIsVolcanoSectionInView] = useState(false);
  const [isExperienceSectionInView, setIsExperienceSectionInView] = useState(false);
  const volcanoSectionRef = useRef<HTMLDivElement>(null);
  const experienceSectionRef = useRef<HTMLDivElement>(null);

  const images = [
    '/images/webflow/caston-001.jpg',
    '/images/webflow/caston-002.jpg',
    '/images/webflow/Monkey.jpg',
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalTime = 5000; // 5 seconds

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, intervalTime);

    return () => clearInterval(interval);
  }, [images.length]);

  useEffect(() => {
    const volcanoObserver = new IntersectionObserver(
      ([entry]) => {
        setIsVolcanoSectionInView(entry.isIntersecting);
      },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    );

    const experienceObserver = new IntersectionObserver(
      ([entry]) => {
        setIsExperienceSectionInView(entry.isIntersecting);
      },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    );

    if (volcanoSectionRef.current) {
      volcanoObserver.observe(volcanoSectionRef.current);
    }

    if (experienceSectionRef.current) {
      experienceObserver.observe(experienceSectionRef.current);
    }

    return () => {
      if (volcanoSectionRef.current) {
        volcanoObserver.unobserve(volcanoSectionRef.current);
      }
      if (experienceSectionRef.current) {
        experienceObserver.unobserve(experienceSectionRef.current);
      }
    };
  }, []);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      (prevIndex + 1) % images.length
    );
  };

  const experiences = [
    {
      title: "Gorilla trekking",
      description: "Gorilla trekking in the Volcanoes National Park, Rwanda, is a once-in-a-lifetime experience that should be on everyone's bucket list. The lush, misty rainforest provides a stunning backdrop for visitors as they track the endangered mountain gorillas. This unforgettable adventure offers an opportunity to witness these incredible creatures in their natural habitat while supporting conservation efforts.",
      imagePosition: "right",
      imageSrc: "/images/webflow/juvinniel.jpg",
      imageAlt: "Mountain Gorilla in Volcanoes National Park"
    },
    {
      title: "Hiking trails",
      description: "Gorilla trekking in the Volcanoes National Park, Rwanda, is a once-in-a-lifetime experience that should be on everyone's bucket list. The lush, misty rainforest provides a stunning backdrop for visitors as they track the endangered mountain gorillas. This unforgettable adventure offers an opportunity to witness these incredible creatures in their natural habitat while supporting conservation efforts.",
      imagePosition: "left",
      imageSrc: "/images/webflow/Caston-monkey.jpg",
      imageAlt: "Hiking trails in Rwanda"
    },
    {
      title: "Golden Monkey Trekking",
      description: "Gorilla trekking in the Volcanoes National Park, Rwanda, is a once-in-a-lifetime experience that should be on everyone's bucket list. The lush, misty rainforest provides a stunning backdrop for visitors as they track the endangered mountain gorillas. This unforgettable adventure offers an opportunity to witness these incredible creatures in their natural habitat while supporting conservation efforts.",
      imagePosition: "right",
      imageSrc: "/images/webflow/gaston-hiking.jpg",
      imageAlt: "Golden Monkey in natural habitat"
    },
    {
      title: "Akagera National Park",
      description: "Gorilla trekking in the Volcanoes National Park, Rwanda, is a once-in-a-lifetime experience that should be on everyone's bucket list. The lush, misty rainforest provides a stunning backdrop for visitors as they track the endangered mountain gorillas. This unforgettable adventure offers an opportunity to witness these incredible creatures in their natural habitat while supporting conservation efforts.",
      imagePosition: "left",
      imageSrc: "/images/webflow/juvinniel.jpg",
      imageAlt: "Akagera National Park landscape"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <LandingNavbar />
      <div className="relative w-full h-[60vh] overflow-hidden mt-2">
      {images.map((image, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={image}
            alt={`Carousel image ${index + 1}`}
            className="w-full h-full object-cover"
          />
          {/* Text overlay for first image */}
          {index === 0 && (
            <>
               <div className="absolute top-4 right-4 md:top-6 md:right-6">
                <a 
                  href="tel:+250791874212"
                  className="block bg-amber-100 px-4 py-2 hover:opacity-90 transition-opacity cursor-pointer" 
                  style={{ backgroundColor: '#d4a574' }}
                >
                  <p className="text-white font-semibold text-sm md:text-base">
                    Call now + 250 791 874 212
                  </p>
                </a>
              </div>
              <div className="absolute bottom-0 left-0 right-0 flex items-end justify-center pb-8 md:pb-12">
              
              <div className="text-center text-white px-4">

                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold drop-shadow-lg">
                Volcanoes National Park
                </h2>
              </div>
            </div>
            </>
          
          )}
          
          {/* Text overlay for second image (Gor1.jpg) */}
          {index === 1 && (
            <>
              {/* Phone number at top right */}
           
              
              {/* Main headline at bottom */}
              <div className="absolute bottom-0 left-0 right-0 flex items-end justify-center pb-8 md:pb-12">
                <div className="text-center text-white px-4">
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold drop-shadow-lg">
                  Discover the Magic of Rwanda's Wildlife with IKORO House
                  </h2>
                </div>
              </div>
            </>
          )}
          
          {/* Text overlay for third image (Cast.jpg) */}
          {index === 2 && (
            <div className="absolute bottom-0 left-0 right-0 flex items-end justify-center pb-8 md:pb-12">
              <div className="text-center text-white px-4">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold drop-shadow-lg">
                Discover the Magic of Rwanda's Wildlife with Ikoro house
                </h2>
              </div>
            </div>
          )}
        </div>
      ))}
      
      {/* Left Arrow */}
      <button
        onClick={goToPrevious}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all z-10"
        aria-label="Previous image"
      >
        <ChevronLeft size={24} />
      </button>

      {/* Right Arrow */}
      <button
        onClick={goToNext}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all z-10"
        aria-label="Next image"
      >
        <ChevronRight size={24} />
      </button>
      
      {/* Optional: Dots indicator */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentIndex
                ? 'bg-white w-8'
                : 'bg-white/50 hover:bg-white/75'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
    <div className="w-full bg-white py-12 px-4 flex flex-col gap-8">


      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Large Left Image */}
          <div className="lg:row-span-2">
            <img
              src="/images/webflow/caston.jpg"
              alt="Rwanda landscape with mountains"
              className="w-full h-[400px] lg:h-full object-cover"
            />
          </div>

          {/* Top Right - Zebras */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <img
              src="/images/webflow/caston-003.jpg"
              alt="Zebras in the wild"
              className="w-full h-[190px] object-cover"
            />
              <img
              src="/images/webflow/caston-004.jpg"
              alt="Mountain gorilla in forest"
              className="w-full h-[190px] object-cover"
            />
          </div>

          {/* Bottom Right Grid - 2 Gorilla Images */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <img
              src="/images/webflow/Monkey.jpg"
              alt="Mountain gorilla in forest"
              className="w-full h-[190px] object-cover"
            />
            <img
              src="/images/webflow/Gor1.jpg"
              alt="Gorilla family"
              className="w-full h-[190px] object-cover"
            />
          </div>
        </div>
      </div>
      
      <div 
        ref={volcanoSectionRef}
        className="max-w-8xl mx-auto bg-[#000000] py-12 px-4 sm:px-6 lg:px-8 transition-all duration-1000 ease-out"
        style={{
          opacity: isVolcanoSectionInView ? 1 : 0,
          transform: isVolcanoSectionInView 
            ? 'perspective(1000px) translateZ(0) scale(1)' 
            : 'perspective(1000px) translateZ(-300px) scale(0.6)'
        }}
      >
        {/* Title */}
        <h2 className="text-white text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-8">
          Experience volcanoes National Park
        </h2>

        {/* Description */}
        <p className="text-white text-base md:text-lg leading-relaxed text-left">
          Volcanoes National Park in northwest Rwanda is a top tourist spot, known for its rare mountain gorillas. Reopened in 1999, it's become increasingly popular. With less than 700 gorillas left, tourists flock to see them. But the park offers more, including scenic vistas and vegetation. It spans 125km2, part of the Virunga Conservation Area, with five volcanoes, including Karisimbi and Bisoke, which can be climbed in one or two days.
        </p>
      </div>
    
    </div>

    {/* Experience Cards Section */}
    <div ref={experienceSectionRef} className="w-full bg-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {experiences.map((experience, index) => (
          <ExperienceCard
            key={index}
            title={experience.title}
            description={experience.description}
            imagePosition={experience.imagePosition}
            imageSrc={experience.imageSrc}
            imageAlt={experience.imageAlt}
            isInView={isExperienceSectionInView}
          />
        ))}
      </div>
    </div>
    
      <LandingFooter />
    </div>
  );
}